/*
 * Accessibility widget — Symmetry theme (private, self-hosted).
 *
 * <accessibility-widget> wraps a floating trigger button (.a11y-fab) and a
 * side panel (.a11y-panel). Every visitor choice is stored in localStorage
 * ('a11y:state') and applied as classes / CSS variables on <html> (and
 * #a11y-root for content-only effects). The head snippet re-applies the same
 * classes before first paint so there is no flash; this component adds the
 * interactivity plus the DOM side-effects (image alt->title, pausing media).
 *
 * All user-facing text comes from data-* attributes (populated by Liquid via
 * the `t` filter) so nothing is hard-coded here — keeps en/he/ru in the
 * locale files.
 */
(function () {
  if (window.customElements && customElements.get('accessibility-widget')) return;

  var STORAGE_KEY = 'a11y:state';
  var FONT_MIN = 0.8;
  var FONT_MAX = 1.6;
  var FONT_STEP = 0.1;
  var ZOOM_ON = 1.35;

  var DEFAULTS = {
    v: 1,
    keyboardNav: false,
    stopMotion: false,
    contrast: 'none', // none | mono | light | invert
    fontScale: 1, // FONT_MIN .. FONT_MAX
    readableFont: false,
    highlightLinks: false,
    highlightHeadings: false,
    imageDesc: false,
    cursor: 'none', // none | white | black
    zoom: 1 // 1 or ZOOM_ON
  };

  function round1(n) {
    return Math.round(n * 10) / 10;
  }

  function defaults() {
    return JSON.parse(JSON.stringify(DEFAULTS));
  }

  function contentRoot() {
    return document.getElementById('a11y-root') || document.body;
  }

  /**
   * Toggle the a11y-* classes and set the CSS variables from a state object.
   * Mirrors the minimal logic in snippets/accessibility-head.liquid.
   */
  function applyClasses(state) {
    var el = document.documentElement;
    var cl = el.classList;
    cl.toggle('a11y-keyboard-nav', !!state.keyboardNav);
    cl.toggle('a11y-stop-motion', !!state.stopMotion);
    cl.toggle('a11y-contrast-mono', state.contrast === 'mono');
    cl.toggle('a11y-contrast-light', state.contrast === 'light');
    cl.toggle('a11y-contrast-invert', state.contrast === 'invert');
    cl.toggle('a11y-readable-font', !!state.readableFont);
    cl.toggle('a11y-highlight-links', !!state.highlightLinks);
    cl.toggle('a11y-highlight-headings', !!state.highlightHeadings);
    cl.toggle('a11y-image-desc', !!state.imageDesc);
    cl.toggle('a11y-cursor-white', state.cursor === 'white');
    cl.toggle('a11y-cursor-black', state.cursor === 'black');
    el.style.setProperty('--a11y-font-scale', state.fontScale || 1);
    el.style.setProperty('--a11y-zoom', state.zoom || 1);
  }

  class AccessibilityWidget extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.fab = this.querySelector('.a11y-fab');
      this.panel = this.querySelector('.a11y-panel');
      this.closeBtn = this.querySelector('.a11y-panel__close');
      this.statusEl = this.querySelector('[data-a11y-status]');
      this.overlay = document.querySelector('.js-overlay');

      this.strOn = this.getAttribute('data-str-on') || 'on';
      this.strOff = this.getAttribute('data-str-off') || 'off';
      this.strReset = this.getAttribute('data-str-reset') || '';
      this.strTextSize = this.getAttribute('data-str-textsize') || '';

      this.state = this.loadState();

      // State classes were set pre-paint by the head snippet; re-assert and
      // run the DOM-dependent side-effects now that the body exists.
      applyClasses(this.state);
      this.applyImageDesc(this.state.imageDesc);
      this.applyMediaPause(this.state.stopMotion);
      this.syncControls();

      this.bindEvents();
    }

    bindEvents() {
      this.onFabClick = () => {
        this.hasAttribute('open') ? this.close() : this.open();
      };
      this.fab.addEventListener('click', this.onFabClick);

      // One delegated handler for every control + the close button.
      this.onPanelClick = (evt) => {
        if (evt.target.closest('.js-a11y-close')) {
          evt.preventDefault();
          this.close();
          return;
        }
        const btn = evt.target.closest('[data-a11y-action]');
        if (btn) {
          evt.preventDefault();
          this.handleAction(btn);
        }
      };
      this.panel.addEventListener('click', this.onPanelClick);

      this.onKeydown = (evt) => {
        if (evt.key === 'Escape' && this.hasAttribute('open')) this.close();
      };
      this.onOverlayClick = () => this.close();
      this.onTrap = (evt) => {
        if (evt.key !== 'Tab') return;
        const f = this.getFocusable();
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (evt.shiftKey && document.activeElement === first) {
          evt.preventDefault();
          last.focus();
        } else if (!evt.shiftKey && document.activeElement === last) {
          evt.preventDefault();
          first.focus();
        }
      };
    }

    getFocusable() {
      return Array.prototype.filter.call(
        this.panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ),
        (el) => el.offsetParent !== null
      );
    }

    open() {
      if (this.overlay) this.overlay.classList.add('is-visible');
      this.setAttribute('open', '');
      this.panel.setAttribute('aria-hidden', 'false');
      this.fab.setAttribute('aria-expanded', 'true');
      this.opener = document.activeElement && document.activeElement.focus ? document.activeElement : this.fab;

      document.addEventListener('keydown', this.onKeydown);
      this.panel.addEventListener('keydown', this.onTrap);
      if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);

      const target = this.closeBtn || this.getFocusable()[0];
      if (target) target.focus();
    }

    close() {
      this.removeAttribute('open');
      this.panel.setAttribute('aria-hidden', 'true');
      this.fab.setAttribute('aria-expanded', 'false');
      if (this.overlay) this.overlay.classList.remove('is-visible');

      document.removeEventListener('keydown', this.onKeydown);
      this.panel.removeEventListener('keydown', this.onTrap);
      if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);

      const back = this.opener || this.fab;
      if (back && back.focus) back.focus();
    }

    handleAction(btn) {
      const parts = (btn.getAttribute('data-a11y-action') || '').split(':');
      const type = parts[0];
      const arg = parts[1];
      const s = this.state;
      let announceState = null; // true/false for on/off toggles

      switch (type) {
        case 'toggle':
          s[arg] = !s[arg];
          announceState = s[arg];
          break;
        case 'contrast':
          s.contrast = s.contrast === arg ? 'none' : arg;
          announceState = s.contrast === arg;
          break;
        case 'cursor':
          s.cursor = s.cursor === arg ? 'none' : arg;
          announceState = s.cursor === arg;
          break;
        case 'font':
          if (arg === 'inc') s.fontScale = round1(Math.min(FONT_MAX, (s.fontScale || 1) + FONT_STEP));
          else if (arg === 'dec') s.fontScale = round1(Math.max(FONT_MIN, (s.fontScale || 1) - FONT_STEP));
          break;
        case 'zoom':
          s.zoom = s.zoom > 1 ? 1 : ZOOM_ON;
          announceState = s.zoom > 1;
          break;
        case 'reset':
          this.reset();
          return;
        default:
          return;
      }

      applyClasses(s);
      if (type === 'toggle' && arg === 'imageDesc') this.applyImageDesc(s.imageDesc);
      if (type === 'toggle' && arg === 'stopMotion') this.applyMediaPause(s.stopMotion);
      this.saveState();
      this.syncControls();

      if (type === 'font') {
        this.announce(this.strTextSize.replace('%value%', String(Math.round(s.fontScale * 100))));
      } else if (announceState !== null) {
        this.announce(this.labelOf(btn) + ' ' + (announceState ? this.strOn : this.strOff));
      }
    }

    reset() {
      this.applyImageDesc(false);
      this.applyMediaPause(false);
      this.state = defaults();
      applyClasses(this.state);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {}
      this.syncControls();
      if (this.strReset) this.announce(this.strReset);
      const first = this.getFocusable()[0];
      if (first) first.focus();
    }

    labelOf(btn) {
      const l = btn.querySelector('.a11y-btn__label');
      return (l ? l.textContent : btn.textContent || '').trim();
    }

    announce(msg) {
      if (!this.statusEl || !msg) return;
      const node = this.statusEl;
      node.textContent = '';
      // Re-set after a tick so screen readers re-announce identical messages.
      window.setTimeout(() => {
        node.textContent = msg;
      }, 30);
    }

    syncControls() {
      const s = this.state;
      const btns = this.querySelectorAll('[data-a11y-action]');
      for (let i = 0; i < btns.length; i++) {
        const btn = btns[i];
        const parts = (btn.getAttribute('data-a11y-action') || '').split(':');
        const type = parts[0];
        const arg = parts[1];
        let active = null;
        let attr = 'aria-pressed';
        if (type === 'toggle') {
          active = !!s[arg];
        } else if (type === 'contrast') {
          active = s.contrast === arg;
          attr = 'aria-checked';
        } else if (type === 'cursor') {
          active = s.cursor === arg;
          attr = 'aria-checked';
        } else if (type === 'zoom') {
          active = s.zoom > 1;
        }
        if (active !== null) btn.setAttribute(attr, active ? 'true' : 'false');
      }
    }

    /* ---- DOM side-effects ---------------------------------------------- */

    applyImageDesc(on) {
      const imgs = contentRoot().querySelectorAll('img');
      for (let i = 0; i < imgs.length; i++) {
        const img = imgs[i];
        if (on) {
          if (img.alt && img.alt.trim() && !img.hasAttribute('data-a11y-title-orig')) {
            img.setAttribute('data-a11y-title-orig', img.getAttribute('title') || '');
            img.title = img.alt;
          }
        } else if (img.hasAttribute('data-a11y-title-orig')) {
          const orig = img.getAttribute('data-a11y-title-orig');
          if (orig) img.title = orig;
          else img.removeAttribute('title');
          img.removeAttribute('data-a11y-title-orig');
        }
      }
      this.observeImages(on);
    }

    observeImages(on) {
      if (on) {
        if (this.imgObserver) return;
        this.imgObserver = new MutationObserver(() => {
          const imgs = contentRoot().querySelectorAll('img:not([data-a11y-title-orig])');
          for (let i = 0; i < imgs.length; i++) {
            const img = imgs[i];
            if (img.alt && img.alt.trim()) {
              img.setAttribute('data-a11y-title-orig', img.getAttribute('title') || '');
              img.title = img.alt;
            }
          }
        });
        this.imgObserver.observe(contentRoot(), { childList: true, subtree: true });
      } else if (this.imgObserver) {
        this.imgObserver.disconnect();
        this.imgObserver = null;
      }
    }

    applyMediaPause(on) {
      const vids = document.querySelectorAll('video');
      for (let i = 0; i < vids.length; i++) {
        const v = vids[i];
        if (on) {
          if (!v.paused) {
            v.setAttribute('data-a11y-was-playing', '1');
            try {
              v.pause();
            } catch (e) {}
          }
        } else if (v.getAttribute('data-a11y-was-playing')) {
          v.removeAttribute('data-a11y-was-playing');
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        }
      }
    }

    /* ---- Persistence --------------------------------------------------- */

    loadState() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.v === 1) {
            const merged = defaults();
            for (const k in parsed) {
              if (Object.prototype.hasOwnProperty.call(merged, k)) merged[k] = parsed[k];
            }
            return merged;
          }
        }
      } catch (e) {}
      return defaults();
    }

    saveState() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      } catch (e) {}
    }
  }

  customElements.define('accessibility-widget', AccessibilityWidget);
})();
